import {
  FeedItemDto,
  MemberTransferRequestDto,
  UserMemberStatusDto,
} from '@act/shared/data-transfer-objects';
import { CircularProgress, Button, Typography } from '@mui/material';
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  EventItem,
  feedSelectors,
  loadFeedItems,
  selectFeedLoadingStatus,
  selectSelectedFeedFilters,
} from 'redux/slices/feedSlice';
import {
  getMemberStatuses,
  membersAppSelectors,
  selectSelectedMember,
} from 'redux/slices/memberSlice';
import { selectedMemberSelectors } from 'redux/slices/selectedMemberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { LoadingStatus } from 'redux/types/shared-types';
import AuditTransferRequestDialog from '../../dialog-forms/AuditTransferRequestDialog';
import FeedFilters from './feed-filters';
import { FilterDiv, ScrollableStyled } from './feed-styles';
import SingleFeedItem from './single-feed-item';
import MemberStatusEvent from './member-status-event';
import { FeedFilter } from 'redux/types/feedTypes';
import StyledAlert from 'toolkit/alerts/styled-alert';
import {
  getScheduledMessagesByMember,
  selectScheduledMessagesByMember,
} from 'redux/slices/messagesSlice';
import { ClockIcon } from '@mui/x-date-pickers/icons';
import ScheduledMessageDrawer from 'react-components/member-page/messaging-drawer/ScheduledMessageDrawer';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { useTracking } from 'react-components/tracking';
import { FlexContainer } from '@act/storybook';
import { useMobile } from 'utilities/mobile-context';

const deriveKey = (eventItem: EventItem) => {
  if (eventItem.type === 'feedItem') {
    const item = eventItem.item as FeedItemDto;
    return `feedItem-${item.externalItemType}-${item.externalItemId}-${item.id}`;
  } else {
    const item = eventItem.item as UserMemberStatusDto;
    return 'memberStatus' + item.id.toString();
  }
};

const ScrollableFeedItems = () => {
  const scheduledSmsFF = useFeatureFlag('scheduled-sms');
  const { generateTrackingProps } = useTracking();
  const selectedMember = useAppSelector(selectSelectedMember);
  const selectedMemberId = selectedMemberSelectors.useSelectSelectedMemberId();
  const memberStatuses = membersAppSelectors.useSelectAllMemberStatuses();
  const eventItems = feedSelectors.useSelectFeedItemsAndUserMemberStatuses();
  const feedLoading: LoadingStatus = useAppSelector(selectFeedLoadingStatus);
  const dispatch = useAppDispatch();
  const [feedInitialized, setFeedInitialized] = useState<boolean>(false);
  const [highlightedKey, setHighlightedKey] = useState<string>(null);
  const scrollElement = useRef<HTMLDivElement>(null);
  const lastFeedItemRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef<boolean>(false);
  const hasReachedEnd = useRef<boolean>(false);
  const previousFeedItemCount = useRef<number>(0);
  const [initialRefs, setInitialRefs] = useState<{
    [key in string]: React.RefObject<HTMLDivElement>;
  }>({});
  const [hasInitialData, setHasInitialData] = useState<boolean>(false);
  const [currentTransferRequest, setCurrentTransferRequest] =
    useState<MemberTransferRequestDto>(null);
  const [openAuditTransferDialog, setOpenAuditTransferDialog] =
    useState<boolean>(false);
  const { isMobile } = useMobile();

  const activeFilters = useAppSelector(selectSelectedFeedFilters);

  const [searchParams] = useSearchParams();
  const [feedItemExternalId] = searchParams.getAll('feedItemExternalId');
  const [feedItemExternalType] = searchParams.getAll('feedItemExternalType');
  const [scheduledMessageOpen, setScheduledMessageOpen] = useState(false);
  const selectedMessages = useAppSelector(
    selectScheduledMessagesByMember(selectedMemberId)
  );

  useEffect(() => {
    if (selectedMemberId) {
      dispatch(
        getScheduledMessagesByMember({
          params: { memberId: selectedMemberId },
        })
      );
    }
  }, [selectedMemberId]);

  function handleOpenTransferDialog(
    memberTransferRequest: MemberTransferRequestDto
  ) {
    setCurrentTransferRequest(memberTransferRequest);
    setOpenAuditTransferDialog(true);
  }

  function handleCloseTransferDialog() {
    setCurrentTransferRequest(null);
    setOpenAuditTransferDialog(false);
  }

  const eventItemMap = useMemo(() => {
    const map: { [key: string]: EventItem } = {};
    eventItems.forEach(item => {
      if (item.type === 'feedItem') {
        map[`${item.item.externalItemType}-${item.item.externalItemId}`] = item;
        map[item.item.id] = item;
      }
    });
    return map;
  }, [eventItems]);

  useEffect(() => {
    if (!memberStatuses.length) {
      dispatch(getMemberStatuses({}));
    }
  }, [memberStatuses.length]);

  useEffect(() => {
    if (!selectedMemberId) return;

    // Reset initialization state when member changes
    setHasInitialData(false);
    setFeedInitialized(false);
    setInitialRefs({});

    // Redux will deduplicate multiple dispatches for the same member
    dispatch(loadFeedItems({}));
  }, [selectedMemberId]);

  // Reset loading flag and check if we've reached the end when feed loading completes
  useEffect(() => {
    if (feedLoading === 'success') {
      const feedItems = eventItems.filter(item => item.type === 'feedItem');
      const currentFeedItemCount = feedItems.length;

      // Check if we were loading more and if we got new items
      if (previousFeedItemCount.current > 0) {
        if (currentFeedItemCount === previousFeedItemCount.current) {
          // No new items were added - we've reached the end
          hasReachedEnd.current = true;
        } else {
          // New items were added, reset the end flag
          hasReachedEnd.current = false;
        }
      }

      isLoadingMore.current = false;
    } else if (feedLoading === 'failed') {
      isLoadingMore.current = false;
    }
  }, [feedLoading, eventItems]);

  // Reset end flag when member or filters change
  useEffect(() => {
    hasReachedEnd.current = false;
    previousFeedItemCount.current = 0;
  }, [selectedMemberId, activeFilters]);

  // Force layout recalculation when mobile context changes (e.g., iPad split-screen)
  // This ensures the feed height adjusts properly when switching between desktop and mobile views
  useEffect(() => {
    if (scrollElement.current && hasInitialData) {
      // Trigger a reflow by accessing offsetHeight, then let the browser recalculate layout
      void scrollElement.current.offsetHeight;

      // Small delay to ensure parent containers have finished their layout changes
      const timeoutId = setTimeout(() => {
        if (scrollElement.current) {
          // Force the browser to recalculate scroll container dimensions
          scrollElement.current.style.display = 'none';
          void scrollElement.current.offsetHeight; // Force reflow
          scrollElement.current.style.display = '';
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [isMobile, hasInitialData]);

  useEffect(() => {
    // Wait for feed loading to complete before showing content
    // This prevents flickering when member statuses load before feed items
    // (e.g., when navigating from docket where statuses are already cached)
    if (feedLoading === 'success' && eventItems.length > 0 && !hasInitialData) {
      setHasInitialData(true);
    }

    // Always create refs for new items, even after initial load
    // This ensures refs are available when scrolling to items that load later
    if (feedLoading === 'success' && eventItems.length > 0) {
      setInitialRefs(prevRefs => {
        const newRefs = { ...prevRefs };
        let hasNewRefs = false;

        eventItems.forEach(fi => {
          const key = deriveKey(fi);
          if (!newRefs[key]) {
            newRefs[key] = React.createRef();
            hasNewRefs = true;
          }
        });

        // Only update state if we actually added new refs
        return hasNewRefs ? newRefs : prevRefs;
      });
    }
  }, [feedLoading, eventItems, hasInitialData]);

  // Track the last scroll attempt to prevent infinite loops
  const lastScrollAttemptRef = useRef<string | null>(null);
  const searchingForItemRef = useRef<boolean>(false);

  // Reset feedInitialized when feedItem query params change to allow re-scrolling
  useEffect(() => {
    if (feedItemExternalType && feedItemExternalId) {
      const scrollKey = `${feedItemExternalType}-${feedItemExternalId}`;
      // Only reset if this is a different item than the last scroll attempt
      if (lastScrollAttemptRef.current !== scrollKey) {
        setFeedInitialized(false);
        lastScrollAttemptRef.current = scrollKey;
        searchingForItemRef.current = true;
      }
    } else {
      searchingForItemRef.current = false;
    }
  }, [feedItemExternalType, feedItemExternalId]);

  // Load more items if the target item isn't found in current items
  useEffect(() => {
    if (
      hasInitialData &&
      feedItemExternalType &&
      feedItemExternalId &&
      searchingForItemRef.current &&
      !feedInitialized &&
      feedLoading === 'success'
    ) {
      const mapKey = `${feedItemExternalType}-${feedItemExternalId}`;
      const foundEventItem = eventItemMap[mapKey];

      // If item not found and we haven't reached the end, load more items
      if (!foundEventItem && !hasReachedEnd.current && !isLoadingMore.current) {
        isLoadingMore.current = true;
        previousFeedItemCount.current = eventItems.filter(
          item => item.type === 'feedItem'
        ).length;
        dispatch(loadFeedItems({ earlier: true }));
      }
    }
  }, [
    hasInitialData,
    feedItemExternalType,
    feedItemExternalId,
    eventItemMap,
    feedInitialized,
    feedLoading,
    eventItems,
    dispatch,
  ]);

  useEffect(() => {
    if (hasInitialData && Object.keys(initialRefs).length > 0) {
      let key = null;
      if (feedItemExternalType && feedItemExternalId) {
        const mapKey = `${feedItemExternalType}-${feedItemExternalId}`;
        const foundEventItem = eventItemMap[mapKey];
        if (foundEventItem) {
          key = deriveKey(foundEventItem);
          // Item found, stop searching
          searchingForItemRef.current = false;
        }
      }
      if (key && initialRefs[key]) {
        // Only scroll if we haven't already scrolled to this item
        if (!feedInitialized) {
          // Use a longer timeout to ensure DOM is ready
          const timeoutId = setTimeout(() => {
            const ref = initialRefs[key];
            if (ref?.current) {
              ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
              setTimeout(() => {
                setHighlightedKey(key);
              }, 1000);
              setTimeout(() => {
                setHighlightedKey(null);
              }, 3500);
              // Only set feedInitialized to true if we successfully found and scrolled to an item
              setFeedInitialized(true);
              searchingForItemRef.current = false;
            } else {
              // If ref.current is null, the element isn't mounted yet, retry after a delay
              // Don't set feedInitialized so we can retry
            }
          }, 500);
          return () => clearTimeout(timeoutId);
        }
      } else if (!feedItemExternalType && !feedItemExternalId) {
        // No feedItem params, normal initialization
        setFeedInitialized(true);
        searchingForItemRef.current = false;
      }
      // If we have query params but didn't find the item yet, don't set feedInitialized
      // This allows retrying when the feed loads more items or when eventItemMap updates
    }
  }, [
    hasInitialData,
    initialRefs,
    feedItemExternalId,
    feedItemExternalType,
    eventItemMap,
    feedInitialized,
  ]);

  const scrollToEventFeedItem = useCallback(
    (id: string | number) => {
      const foundEventItem = eventItemMap[id];
      const key = foundEventItem ? deriveKey(foundEventItem) : null;

      if (initialRefs[key]) {
        setHighlightedKey(key);
        initialRefs[key]?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
        setTimeout(() => {
          setHighlightedKey(null);
        }, 3500);
      }
    },
    [eventItemMap, initialRefs]
  );

  // Memoize additional dialogs to prevent breaking SingleFeedItem memoization
  const emptyDialogs = useMemo(() => ({}), []);
  const additionalDialogsForTransfer = useMemo(
    () => ({
      openTransferDialog: handleOpenTransferDialog as (
        ...args: unknown[]
      ) => void,
    }),
    []
  );

  const getAdditionalDialogs = useCallback(
    (
      feedItemType: string
    ): {
      [key: string]: (...args: unknown[]) => void;
    } => {
      switch (feedItemType) {
        case 'member_transfer_request':
          return additionalDialogsForTransfer;
        default:
          return emptyDialogs;
      }
    },
    [additionalDialogsForTransfer, emptyDialogs]
  );

  // Memoize filtered event items to prevent recalculation on every render
  const filteredEventItems = useMemo(() => {
    return eventItems.filter(fi => {
      // If there are no active filters, show all items
      if (activeFilters.length === 0) {
        return true;
      }

      // For member status events, only show when no filters are active
      if (fi.type === 'userMemberStatus') {
        return false;
      }

      // For feed items, filter based on their external item type
      if (fi.type === 'feedItem') {
        const item = fi.item as FeedItemDto;

        // Handle special case mappings
        if (
          activeFilters.includes('hospital events') &&
          item.externalItemType === 'hospitalization'
        ) {
          return true;
        }

        if (activeFilters.includes('task')) {
          const taskTypes = [
            'member_status_change_request',
            'member_transfer_request',
            'assigned_workflow_phase',
          ];
          if (taskTypes.includes(item.externalItemType)) {
            return true;
          }
        }

        // Direct mapping for other filter types
        return activeFilters.includes(item.externalItemType as FeedFilter);
      }

      return false;
    });
  }, [eventItems, activeFilters]);

  // Use IntersectionObserver to detect when last feed item is near viewport
  useEffect(() => {
    // Find the last feed item from filtered items (what's actually rendered)
    const feedItems = filteredEventItems.filter(
      item => item.type === 'feedItem'
    );
    const lastFeedItemKey =
      feedItems.length > 0 ? deriveKey(feedItems[feedItems.length - 1]) : null;

    if (
      !hasInitialData ||
      !lastFeedItemRef.current ||
      !scrollElement.current ||
      !lastFeedItemKey
    ) {
      return;
    }

    // Don't observe if we've reached the end
    if (hasReachedEnd.current) {
      return;
    }

    const lastItemElement = lastFeedItemRef.current;
    const scrollContainer = scrollElement.current;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        const isNearViewport =
          entry.isIntersecting || entry.boundingClientRect.top < 800;

        // Don't load if we've reached the end or are already loading
        if (
          hasReachedEnd.current ||
          isLoadingMore.current ||
          feedLoading === 'loading' ||
          feedLoading === 'initial'
        ) {
          return;
        }

        if (isNearViewport) {
          isLoadingMore.current = true;
          // Save current count before loading to detect if we get new items
          previousFeedItemCount.current = feedItems.length;
          dispatch(loadFeedItems({ earlier: true }));
        }
      },
      {
        root: scrollContainer,
        rootMargin: '800px', // Trigger 800px before last item is visible
        threshold: 0,
      }
    );

    observer.observe(lastItemElement);

    return () => {
      observer.disconnect();
    };
  }, [hasInitialData, filteredEventItems, feedLoading, dispatch]);

  if (!hasInitialData) {
    return (
      <FlexContainer
        direction="row"
        alignItems="center"
        justifyContent="center"
        height="fit-content"
        width="100%"
        padding="24px 0"
      >
        <CircularProgress />
      </FlexContainer>
    );
  } else {
    return (
      <>
        <ScrollableStyled ref={scrollElement}>
          <div className="scroll-content">
            <FilterDiv>
              <FeedFilters />
            </FilterDiv>
            {scheduledSmsFF && selectedMessages?.length > 0 && (
              <StyledAlert
                mode="inline"
                severity="info"
                message={` ${selectedMessages?.length} ${
                  selectedMessages?.length === 1 ? 'text' : 'texts'
                } scheduled`}
                icon={<ClockIcon style={{ height: '20px', width: '20px' }} />}
                actions={
                  <Button
                    onClick={() => setScheduledMessageOpen(true)}
                    sx={{
                      padding: '4px',
                    }}
                    {...generateTrackingProps('open-scheduled-messages-drawer')}
                  >
                    <Typography variant="bodyMedium">View</Typography>
                  </Button>
                }
              />
            )}
            {!!selectedMember?.phoneNumbers?.find(
              phoneNumber =>
                !!phoneNumber.doNotContact &&
                phoneNumber.doNotContact?.reason === '"STOP" message'
            ) && (
              <StyledAlert
                message={`This Member texted "STOP" for do not contact. Instruct member to text "START" to opt back in and receive text messages.`}
                severity="info"
                variant="filled"
              />
            )}

            {filteredEventItems.map((fi, index) => {
              const key = deriveKey(fi);
              const partnerKey = `${fi.item.externalItemType}-${fi.item.externalItemId}`;

              if (fi.type === 'userMemberStatus') {
                const item = fi.item as UserMemberStatusDto;
                return (
                  <MemberStatusEvent
                    key={key}
                    userMemberStatus={item}
                    showJumpButton={!!eventItemMap[partnerKey]}
                    previousItem={filteredEventItems[index - 1]}
                    scrollToEventFeedItem={scrollToEventFeedItem}
                  />
                );
              }

              if (fi.type === 'feedItem') {
                const item = fi.item as FeedItemDto;
                const previousTimestamp =
                  filteredEventItems[index - 1]?.timestamp || 0;

                // Find the last feed item to attach the observer ref
                const feedItems = filteredEventItems.filter(
                  item => item.type === 'feedItem'
                );
                const isLastFeedItem =
                  feedItems.length > 0 &&
                  deriveKey(feedItems[feedItems.length - 1]) === key;

                return (
                  <SingleFeedItem
                    key={key}
                    ref={isLastFeedItem ? lastFeedItemRef : initialRefs[key]}
                    feedItem={item}
                    previousTimestamp={previousTimestamp}
                    isLastEventItem={index === 0}
                    shouldHighlight={key === highlightedKey}
                    additionalDialogs={getAdditionalDialogs(
                      item.externalItemType
                    )}
                  ></SingleFeedItem>
                );
              }
            })}
            <AuditTransferRequestDialog
              requestMember={selectedMember}
              transferRequest={currentTransferRequest}
              open={openAuditTransferDialog}
              handleClose={handleCloseTransferDialog}
            />
          </div>
        </ScrollableStyled>
        {scheduledSmsFF && (
          <ScheduledMessageDrawer
            selectedMember={selectedMember}
            open={scheduledMessageOpen}
            onClose={() => setScheduledMessageOpen(false)}
            scheduledMessages={selectedMessages
              ?.filter(message => message.sendAt != null)
              ?.sort((a, b) => a.sendAt - b.sendAt)}
          />
        )}
      </>
    );
  }
};

export default ScrollableFeedItems;
